<template>
  <div class="lia-wizard">
    <!-- Persistent "not legal advice" strip — on every compliance screen (decision a) -->
    <div class="legal-strip">
      <CygnusIcon name="shield" />
      <span><strong>This is documentation, not legal advice.</strong> Yorlin structures and stores the decisions <strong>you</strong> make so you have defensible paperwork on file. It does not tell you whether your outreach is lawful — for that, get a professional review.</span>
    </div>

    <!-- Loading -->
    <div v-if="gateLocked === null || (gateLocked === false && !assessment)" class="lia-loading">
      <CygnusIcon name="spinner" :spin="true" /> Loading…
    </div>

    <!-- Growth+ teaser for an ungated tenant -->
    <div v-else-if="gateLocked === true" class="lia-teaser">
      <div class="starter-avail">
        <CygnusIcon name="shield" />
        <span><strong>Available on your plan right now:</strong> one-click unsubscribe on every send, per-sender opt-out honouring, platform-wide bounce/complaint suppression, the cross-tenant volume guard, verified-sender enforcement, per-subscriber basis recording, and the honest "not legal advice" framing. <strong>Compliance safeguards are never behind a paywall.</strong> What Growth+ adds is the productised paperwork.</span>
      </div>
      <div class="upgrade">
        <div class="upgrade__ic"><CygnusIcon name="shield" /></div>
        <div class="upgrade__eyebrow">Growth+</div>
        <h3>Turn your safeguards into paperwork you can show</h3>
        <p>You're already sending responsibly. The Compliance wizard writes it down — a plain-English Legitimate Interests Assessment, a "PECR-checked" badge on your campaigns, and a dated, versioned record you can produce if anyone ever asks.</p>
        <div class="upgrade__benefits">
          <div class="benefit"><CygnusIcon name="check" /><span><strong>Guided LIA wizard</strong> — six plain questions, the ICO's three-part test structure, no legal jargon.</span></div>
          <div class="benefit"><CygnusIcon name="check" /><span><strong>"PECR-checked" badge</strong> — your mechanical safeguards, verified and shown on every campaign.</span></div>
          <div class="benefit"><CygnusIcon name="check" /><span><strong>Versioned document store</strong> — immutable, timestamped, printable, with annual review nudges.</span></div>
        </div>
        <div class="upgrade__actions">
          <a href="/admin/usage-plans" class="btn lia-btn-violet"><CygnusIcon name="bolt" class="me-1" />Upgrade to Growth+</a>
        </div>
      </div>
    </div>

    <!-- The wizard -->
    <div v-else class="wiz">
      <aside class="wiz__rail">
        <div class="wiz__rail-t">Legitimate Interests Assessment</div>
        <div
          v-for="(s, i) in steps"
          :key="s.key"
          class="wstep"
          :class="{ active: i === step, done: i < step }"
          @click="i < step && goTo(i)"
        >
          <span class="wstep__num"><CygnusIcon name="check" v-if="i < step" /><span v-else>{{ i + 1 }}</span></span>
          <div><div class="wstep__t">{{ s.title }}</div><div class="wstep__s">{{ s.sub }}</div></div>
        </div>
      </aside>

      <div class="wiz__body">
        <div class="wiz__progress">Step {{ step + 1 }} of {{ steps.length }} · {{ steps[step].title }}</div>

        <!-- Step 1 — audience -->
        <div v-if="step === 0">
          <h2 class="wiz__q">Who are you contacting, and are they businesses or individuals?</h2>
          <p class="wiz__hint">Under PECR, who you email changes the rules. This records your audience in your own words and flags the distinction that trips people up most — sole traders and some partnerships count as <em>individuals</em>, not corporate contacts.</p>
          <div class="wiz__grid">
            <div>
              <div class="field">
                <label class="field__l">Give this assessment a name</label>
                <input v-model="form.title" class="form-input" placeholder="e.g. Cotswolds independent-business outreach" @input="scheduleSave">
              </div>
              <div class="field">
                <label class="field__l">Describe the audience for this outreach</label>
                <textarea v-model="form.answers.audience_description" class="form-input" rows="3" placeholder="e.g. Owners of independent hospitality & retail businesses across the Cotswolds…" @input="onAudienceInput"></textarea>
                <div class="field__note">Plain words are fine. This becomes the "audience" line of your LIA record.</div>
              </div>
              <div class="field">
                <label class="field__l">Short audience label (shown on the badge)</label>
                <input v-model="form.audience_label" class="form-input" placeholder="e.g. Cotswolds hospitality & retail" @input="scheduleSave">
              </div>
              <div class="field">
                <label class="field__l">What kind of contacts are on this list?</label>
                <div
                  v-for="opt in contactTypes"
                  :key="opt.key"
                  class="choice"
                  :class="{ sel: form.answers.contact_type === opt.key }"
                  @click="setContactType(opt.key)"
                >
                  <span class="choice__radio"></span>
                  <div><div class="choice__t">{{ opt.title }}</div><div class="choice__d">{{ opt.desc }}</div></div>
                  <span class="choice__tag" :class="opt.tagClass">{{ opt.tag }}</span>
                </div>
              </div>
              <div v-if="form.answers.contact_type === 'mixed' || form.answers.contact_type === 'individuals'" class="nuance">
                <CygnusIcon name="warning" />
                <span><strong>The honest bit:</strong> PECR treats a <strong>sole trader or an individual partner</strong> like a consumer — marketing email to them needs <strong>consent</strong>, even though it feels like B2B. Limited companies and LLPs are corporate subscribers you can email under legitimate interest. If your list mixes the two, keep the sole-trader rows on a consent footing rather than lumping everyone under one basis.</span>
              </div>
            </div>
            <div>
              <div class="why">
                <div class="why__h"><CygnusIcon name="info-circle" /> Why this matters</div>
                <p>PECR draws a line between <strong>corporate subscribers</strong> — where B2B marketing can rely on legitimate interest — and <strong>individual subscribers</strong>, including sole traders, where you generally need consent.</p>
                <div class="why__cite">Reflects ICO guidance on the corporate-subscriber rules in PECR reg. 22. We link the structure; we don't paraphrase it as advice.</div>
              </div>
              <div class="prefill">
                <div class="prefill__h"><CygnusIcon name="bolt" /> What Yorlin already knows</div>
                <div v-for="f in prefillFor(['basis_recorded'])" :key="f.key" class="pfact">
                  <CygnusIcon name="check" /><span><strong>{{ f.title }}.</strong> {{ f.detail }}</span><span class="auto">{{ f.tag }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 2 — purpose -->
        <div v-else-if="step === 1">
          <h2 class="wiz__q">Why are you contacting these businesses, and who benefits?</h2>
          <p class="wiz__hint">The first of the ICO's three tests. In plain terms: is there a real, specific reason for this outreach, and is the benefit legitimate? No legal language needed.</p>
          <div class="wiz__grid">
            <div>
              <div class="field">
                <label class="field__l">What are you trying to achieve with this outreach?</label>
                <textarea v-model="form.answers.purpose" class="form-input" rows="4" @input="scheduleSave"></textarea>
              </div>
              <div class="field">
                <label class="field__l">Is this purpose specific and current — or speculative?</label>
                <div class="choice" :class="{ sel: form.answers.purpose_specificity === 'specific' }" @click="setAnswer('purpose_specificity','specific')">
                  <span class="choice__radio"></span>
                  <div><div class="choice__t">Specific &amp; current</div><div class="choice__d">A defined offer, relevant to the recipient's business, that we're actively delivering now.</div></div>
                </div>
                <div class="choice" :class="{ sel: form.answers.purpose_specificity === 'broad' }" @click="setAnswer('purpose_specificity','broad')">
                  <span class="choice__radio"></span>
                  <div><div class="choice__t">Broad brand awareness</div><div class="choice__d">Weaker footing for legitimate interest — the balancing test gets harder.</div></div>
                </div>
              </div>
              <div class="field">
                <label class="field__l">Would the recipient reasonably expect to hear from you?</label>
                <textarea v-model="form.answers.reasonable_expectations" class="form-input" rows="3" @input="scheduleSave"></textarea>
                <div class="field__note">"Reasonable expectations" is the hinge of the balancing test later — answering it here saves you repeating yourself.</div>
              </div>
            </div>
            <div>
              <div class="why">
                <div class="why__h"><CygnusIcon name="info-circle" /> Why this matters</div>
                <p>The <strong>purpose test</strong> asks whether you've identified a real, specific legitimate interest. Commercial interests count — direct marketing is named in the GDPR as a potential legitimate interest.</p>
                <div class="why__cite">Structure follows the ICO's three-part LIA (purpose / necessity / balancing). We reproduce the structure, not legal conclusions.</div>
              </div>
              <div class="prefill">
                <div class="prefill__h"><CygnusIcon name="bolt" /> Prefilled from your setup</div>
                <div v-for="f in prefillFor(['sender_identity'])" :key="f.key" class="pfact">
                  <CygnusIcon name="check" /><span><strong>{{ f.title }}.</strong> {{ f.detail }}</span><span class="auto">{{ f.tag }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 3 — necessity + balancing -->
        <div v-else-if="step === 2">
          <h2 class="wiz__q">Is email necessary, and does your interest outweigh the recipient's privacy?</h2>
          <p class="wiz__hint">The last two tests together. <strong>Necessity:</strong> is direct email a reasonable way to achieve your purpose? <strong>Balancing:</strong> weigh your interest against the recipient's rights and reasonable expectations.</p>
          <div class="wiz__grid">
            <div>
              <div class="field">
                <label class="field__l"><strong>Necessity</strong> — why is email a proportionate way to reach these businesses?</label>
                <textarea v-model="form.answers.necessity" class="form-input" rows="3" @input="scheduleSave"></textarea>
              </div>
              <div class="field">
                <label class="field__l"><strong>Balancing</strong> — what's the impact on the recipient, and how do you keep it low?</label>
                <textarea v-model="form.answers.balancing" class="form-input" rows="4" @input="scheduleSave"></textarea>
                <div class="field__note">Be honest about impact — a strong balancing answer acknowledges the recipient's side, it doesn't dismiss it.</div>
              </div>
              <div class="field">
                <label class="field__l">Any recipients you'd <em>not</em> rely on legitimate interest for?</label>
                <textarea v-model="form.answers.exclusions" class="form-input" rows="2" placeholder="e.g. Sole traders & personal addresses are kept on a consent-only footing and excluded." @input="scheduleSave"></textarea>
              </div>
            </div>
            <div>
              <div class="why">
                <div class="why__h"><CygnusIcon name="info-circle" /> Why this matters</div>
                <p>The <strong>three-part test</strong> — purpose, necessity, balancing — is the ICO's own structure. Passing all three, and being able to show your working, is what makes legitimate interest defensible.</p>
                <div class="why__cite">Mirrors the ICO's three-part LIA template structure. The words in the boxes are yours; the framework is theirs.</div>
              </div>
              <div class="prefill">
                <div class="prefill__h"><CygnusIcon name="bolt" /> Safeguards we can prove for you</div>
                <div v-for="f in prefillFor(['unsubscribe','opt_out_sender_scoped','free_text_optout'])" :key="f.key" class="pfact">
                  <CygnusIcon name="check" /><span><strong>{{ f.title }}.</strong> {{ f.detail }}</span><span class="auto">{{ f.tag }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 4 — safeguards (read-only facts + tenant additions) -->
        <div v-else-if="step === 3">
          <h2 class="wiz__q">The safeguards on your sends — mostly prefilled</h2>
          <p class="wiz__hint">These are safeguards Yorlin already enforces on every marketing send. They're recorded automatically as evidence in your assessment — you don't have to restate them. Add any of your own below.</p>
          <div class="safeguards">
            <div v-for="f in facts" :key="f.key" class="sg">
              <CygnusIcon name="check" /><span><strong>{{ f.title }}.</strong> {{ f.detail }}</span><span class="auto">{{ f.tag }}</span>
            </div>
          </div>
          <div class="field" style="margin-top:16px">
            <label class="field__l">Any additional safeguards you apply yourself? (optional)</label>
            <textarea v-model="form.answers.tenant_safeguards" class="form-input" rows="3" placeholder="e.g. We cap follow-ups at two, and review the list quarterly." @input="scheduleSave"></textarea>
          </div>
        </div>

        <!-- Step 5 — review cadence -->
        <div v-else-if="step === 4">
          <h2 class="wiz__q">When will you review this assessment?</h2>
          <p class="wiz__hint">An LIA should be reviewed at least annually, or sooner if your audience, purpose or method changes. Yorlin will nudge you 30 days before the review date.</p>
          <div class="field">
            <div class="choice sel"><span class="choice__radio"></span><div><div class="choice__t">Annually (recommended)</div><div class="choice__d">We'll set a review date one year from generation and remind you 30 days before.</div></div></div>
          </div>
          <div class="field">
            <label class="field__l">Notes on what would trigger an earlier review (optional)</label>
            <textarea v-model="form.answers.review_notes" class="form-input" rows="2" placeholder="e.g. A change of audience, a new offer, or a spike in complaints." @input="scheduleSave"></textarea>
          </div>
        </div>

        <!-- Step 6 — generate -->
        <div v-else-if="step === 5">
          <h2 class="wiz__q">Review &amp; generate your record</h2>
          <p class="wiz__hint">Generating stores this as an <strong>immutable, dated version</strong>. To change it later you'll start a new revision — the old version stays on file, so you can always show the assessment as it stood when a campaign ran.</p>
          <div class="review-summary">
            <div class="rs-row"><span class="rs-k">Name</span><span class="rs-v">{{ form.title || '—' }}</span></div>
            <div class="rs-row"><span class="rs-k">Audience</span><span class="rs-v">{{ form.answers.audience_description || '—' }}</span></div>
            <div class="rs-row"><span class="rs-k">Purpose</span><span class="rs-v">{{ form.answers.purpose || '—' }}</span></div>
            <div class="rs-row"><span class="rs-k">Necessity</span><span class="rs-v">{{ form.answers.necessity || '—' }}</span></div>
            <div class="rs-row"><span class="rs-k">Balancing</span><span class="rs-v">{{ form.answers.balancing || '—' }}</span></div>
          </div>
          <div class="disclaimer">
            <CygnusIcon name="shield" />
            <span><strong>Yorlin helps you keep good records — it isn't your lawyer.</strong> This record documents the decisions you've made and the safeguards this platform enforces. It doesn't constitute legal advice and doesn't guarantee that any campaign complies with PECR, UK GDPR or any other law.</span>
          </div>
        </div>

        <div class="wiz__foot">
          <button v-if="step > 0" class="btn btn-secondary" @click="prev"><CygnusIcon name="arrow-left" class="me-1" />Back</button>
          <button v-if="step < steps.length - 1" class="btn btn-primary" @click="next">Continue <CygnusIcon name="arrow-right" class="ms-1" /></button>
          <button v-else class="btn btn-primary" :disabled="busy.generate" @click="generate">
            <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="busy.generate" /><CygnusIcon name="file" class="me-1" v-else />Generate record
          </button>
          <span class="save"><CygnusIcon name="check-circle" :class="{ 'text-muted': !savedRecently }" /> {{ saveLabel }}</span>
        </div>
        <p v-if="error" class="text-danger small mt-2">{{ error }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";

export default {
  name: 'AdminLiaWizard',
  props: { componentData: { type: Object, default: () => ({}) } },
  data() {
    return {
      api: useApi(),
      gateLocked: null,
      assessment: null,
      facts: [],
      step: 0,
      busy: { generate: false },
      error: null,
      savedRecently: false,
      saveTimer: null,
      steps: [
        { key: 'audience',  title: 'Who you contact',      sub: 'Audience & recipient type' },
        { key: 'purpose',   title: 'Purpose & benefit',    sub: 'The "purpose test"' },
        { key: 'balancing', title: 'Necessity & balancing',sub: 'The three-part test' },
        { key: 'safeguards',title: 'Safeguards',           sub: 'Mostly prefilled' },
        { key: 'review',    title: 'Review cadence',       sub: 'When to reassess' },
        { key: 'generate',  title: 'Generate record',      sub: 'Review & store' },
      ],
      contactTypes: [
        { key: 'corporate',   title: 'Mostly limited companies & LLPs', desc: 'Corporate subscribers. B2B marketing email to a corporate body’s generic or role address is the classic legitimate-interest case under PECR.', tag: 'Legit. interest', tagClass: 'li' },
        { key: 'mixed',       title: 'A mix, including sole traders & individuals', desc: 'Some recipients are individuals in law. These need consent for marketing email, not legitimate interest.', tag: 'Needs care', tagClass: 'care' },
        { key: 'individuals', title: 'Named individuals at their personal address', desc: 'Personal inboxes (gmail, outlook.com). Marketing here generally needs prior consent.', tag: 'Consent', tagClass: 'consent' },
      ],
      form: { title: '', audience_label: '', answers: { contact_type: 'corporate', audience_description: '', purpose: '', purpose_specificity: 'specific', reasonable_expectations: '', necessity: '', balancing: '', exclusions: '', tenant_safeguards: '', review_notes: '' } },
    };
  },
  computed: {
    saveLabel() { return this.savedRecently ? 'Draft saved automatically' : 'Changes save as you type'; },
    editId() {
      const m = window.location.pathname.match(/\/admin\/compliance\/lia\/(\d+)\/edit/);
      return m ? parseInt(m[1], 10) : null;
    },
  },
  async mounted() {
    await this.checkGate();
    if (this.gateLocked === false) {
      await this.loadFacts();
      await this.bootstrapDraft();
    }
  },
  methods: {
    async checkGate() {
      try {
        const res = await this.api.get('/api/v1/me/usage');
        const body = res?.data ?? res;
        const entitled = !!body?.features?.compliance_wizard || !!body?.plan?.can_manage;
        this.gateLocked = !entitled;
      } catch (e) { this.gateLocked = false; }
    },
    async loadFacts() {
      try {
        const res = await this.api.get('/api/v1/admin/compliance/facts');
        const body = res?.data ?? res;
        this.facts = Array.isArray(body?.safeguards_facts) ? body.safeguards_facts : [];
      } catch (e) { this.facts = []; }
    },
    async bootstrapDraft() {
      try {
        if (this.editId) {
          const res = await this.api.get(`/api/v1/admin/compliance/lia/${this.editId}`);
          this.hydrate((res?.data ?? res)?.assessment);
        } else {
          const res = await this.api.post('/api/v1/admin/compliance/lia', {});
          const a = (res?.data ?? res)?.assessment;
          this.hydrate(a);
          if (a?.id) window.history.replaceState({}, '', `/admin/compliance/lia/${a.id}/edit`);
        }
      } catch (e) {
        this.error = this.errText(e, 'Could not start the assessment.');
        if (e?.response?.data?.error === 'plan_gated') this.gateLocked = true;
      }
    },
    hydrate(a) {
      if (!a) return;
      this.assessment = a;
      this.form.title = a.title || '';
      this.form.audience_label = a.audience_label || '';
      if (a.answers && typeof a.answers === 'object') {
        this.form.answers = { ...this.form.answers, ...a.answers };
      }
      if (a.safeguards_facts) this.facts = a.safeguards_facts;
    },
    prefillFor(keys) { return this.facts.filter((f) => keys.includes(f.key)); },
    setContactType(k) { this.form.answers.contact_type = k; this.scheduleSave(); },
    setAnswer(k, v) { this.form.answers[k] = v; this.scheduleSave(); },
    onAudienceInput() {
      if (!this.form.audience_label && this.form.answers.audience_description) {
        this.form.audience_label = this.form.answers.audience_description.slice(0, 60);
      }
      this.scheduleSave();
    },
    goTo(i) { this.step = i; },
    next() { if (this.step < this.steps.length - 1) this.step++; this.save(); },
    prev() { if (this.step > 0) this.step--; },
    scheduleSave() {
      this.savedRecently = false;
      if (this.saveTimer) clearTimeout(this.saveTimer);
      this.saveTimer = setTimeout(() => this.save(), 800);
    },
    async save() {
      if (!this.assessment?.id) return;
      try {
        await this.api.put(`/api/v1/admin/compliance/lia/${this.assessment.id}`, {
          title: this.form.title,
          audience_label: this.form.audience_label,
          answers: this.form.answers,
        });
        this.savedRecently = true;
      } catch (e) { /* autosave is best-effort; generate re-validates */ }
    },
    async generate() {
      if (!this.assessment?.id) return;
      this.busy.generate = true; this.error = null;
      try {
        await this.save();
        const res = await this.api.post(`/api/v1/admin/compliance/lia/${this.assessment.id}/generate`, {});
        const a = (res?.data ?? res)?.assessment;
        window.location.href = `/admin/compliance/lia/${a?.id ?? this.assessment.id}`;
      } catch (e) {
        this.error = this.errText(e, 'Could not generate the record.');
      }
      this.busy.generate = false;
    },
    errText(e, fallback) {
      const body = e?.response?.data ?? e?.data;
      if (body?.error === 'plan_gated') return body.message || fallback;
      return body?.message || body?.error || fallback;
    },
  },
};
</script>

<style scoped>
.lia-wizard { padding: 24px; max-width: 1180px; }
.legal-strip { display:flex; align-items:center; gap:11px; padding:10px 18px; background:linear-gradient(180deg,#FBFAFF,#F7F5FF); border:1px solid #E0D2FB; border-radius:10px; font-size:.82rem; color:#4B3A73; line-height:1.4; margin-bottom:18px; }
.legal-strip i { color:#7C3AED; }
.lia-loading { padding:40px; color:#697086; }

.lia-teaser .starter-avail { display:flex; gap:11px; align-items:flex-start; margin-bottom:16px; padding:13px 16px; background:#E4F7F4; border:1px solid #B6EBE4; border-radius:12px; font-size:.86rem; line-height:1.5; color:#0B5D57; }
.lia-teaser .starter-avail i { color:#14B8A6; margin-top:2px; }
.upgrade { max-width:600px; text-align:center; margin:0 auto; background:linear-gradient(135deg,#F3EEFE,#FBFAFF); border:1px solid #E0D2FB; border-radius:16px; padding:30px 34px; }
.upgrade__ic { width:52px; height:52px; border-radius:14px; background:#fff; border:1px solid #E0D2FB; color:#7C3AED; display:flex; align-items:center; justify-content:center; font-size:1.4rem; margin:0 auto 14px; }
.upgrade__eyebrow { font-size:.68rem; font-weight:800; letter-spacing:.08em; text-transform:uppercase; color:#7C3AED; margin-bottom:8px; }
.upgrade h3 { margin:0 0 8px; font-size:1.25rem; }
.upgrade p { margin:0 0 18px; font-size:.9rem; color:#697086; line-height:1.55; }
.upgrade__benefits { text-align:left; display:grid; gap:10px; margin:0 auto 22px; max-width:460px; }
.benefit { display:flex; gap:10px; align-items:flex-start; font-size:.88rem; line-height:1.4; }
.benefit i { color:#7C3AED; margin-top:3px; }
.lia-btn-violet { background:#7C3AED; border:1px solid #7C3AED; color:#fff; text-decoration:none; }
.lia-btn-violet:hover { background:#6D28D9; color:#fff; }

.wiz { display:grid; grid-template-columns:230px 1fr; gap:0; border:1px solid #EAECF3; border-radius:14px; overflow:hidden; background:#fff; }
.wiz__rail { background:linear-gradient(180deg,#FBFCFF,#F7F9FE); border-right:1px solid #EAECF3; padding:20px 16px; }
.wiz__rail-t { font-size:.66rem; font-weight:800; text-transform:uppercase; letter-spacing:.08em; color:#9AA0AC; margin-bottom:14px; }
.wstep { display:flex; gap:11px; padding:9px 8px; border-radius:9px; margin-bottom:2px; }
.wstep.done { cursor:pointer; }
.wstep__num { width:24px; height:24px; border-radius:50%; flex:none; display:flex; align-items:center; justify-content:center; font-size:.72rem; font-weight:700; border:1.5px solid #E0E3EC; background:#fff; color:#9AA0AC; }
.wstep__t { font-size:.82rem; font-weight:600; color:#697086; line-height:1.25; }
.wstep__s { font-size:.7rem; color:#9AA0AC; margin-top:1px; }
.wstep.done .wstep__num { background:#14B8A6; border-color:#14B8A6; color:#fff; }
.wstep.done .wstep__t { color:#1A1C23; }
.wstep.active { background:#fff; box-shadow:0 1px 3px rgba(15,23,42,.08); }
.wstep.active .wstep__num { background:#4F46E5; border-color:#4F46E5; color:#fff; }
.wstep.active .wstep__t { color:#4338CA; }
.wiz__body { padding:24px 28px; }
.wiz__progress { font-size:.72rem; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:#9AA0AC; margin-bottom:5px; }
.wiz__q { font-size:1.22rem; font-weight:700; line-height:1.35; margin:0 0 6px; max-width:600px; }
.wiz__hint { font-size:.9rem; color:#697086; line-height:1.55; max-width:620px; margin:0 0 18px; }
.wiz__grid { display:grid; grid-template-columns:1fr 300px; gap:24px; }
@media(max-width:1000px){ .wiz__grid{ grid-template-columns:1fr; } .wiz{ grid-template-columns:1fr; } }
.field { margin-bottom:16px; }
.field__l { font-size:.82rem; font-weight:600; margin-bottom:7px; display:block; }
.field__note { font-size:.75rem; color:#9AA0AC; margin-top:5px; line-height:1.4; }
.form-input { width:100%; border:1px solid #E0E3EC; background:#fff; border-radius:8px; font-size:.9rem; padding:9px 11px; color:#1A1C23; font-family:inherit; line-height:1.5; }
textarea.form-input { resize:vertical; }
.form-input:focus { outline:none; border-color:#4F46E5; box-shadow:0 0 0 3px #EEF0FF; }
.choice { display:flex; gap:11px; align-items:flex-start; border:1px solid #E0E3EC; border-radius:10px; padding:12px 14px; cursor:pointer; background:#fff; margin-bottom:9px; }
.choice.sel { border-color:#4F46E5; background:linear-gradient(180deg,#EEF0FF,#fff); box-shadow:0 0 0 1px #4F46E5; }
.choice__radio { width:18px; height:18px; border-radius:50%; border:2px solid #E0E3EC; flex:none; margin-top:1px; position:relative; }
.choice.sel .choice__radio { border-color:#4F46E5; }
.choice.sel .choice__radio::after { content:""; position:absolute; inset:3px; border-radius:50%; background:#4F46E5; }
.choice__t { font-size:.88rem; font-weight:600; }
.choice__d { font-size:.79rem; color:#697086; line-height:1.45; margin-top:2px; }
.choice__tag { font-size:.6rem; font-weight:800; text-transform:uppercase; letter-spacing:.04em; padding:2px 7px; border-radius:999px; margin-left:auto; flex:none; height:fit-content; }
.choice__tag.li { background:#E4F7F4; color:#0F766E; }
.choice__tag.consent { background:#EEF0FF; color:#4338CA; }
.choice__tag.care { background:#FFF4E6; color:#9A5B12; }
.nuance { display:flex; gap:9px; align-items:flex-start; margin:2px 0 16px; padding:11px 13px; background:#FFF4E6; border:1px solid #F0DCBB; border-radius:10px; font-size:.8rem; color:#9A5B12; line-height:1.5; }
.nuance i { flex:none; margin-top:2px; }
.why { background:linear-gradient(180deg,#F7F9FE,#FCFDFF); border:1px solid #EAECF3; border-radius:12px; padding:15px 16px; }
.why__h { font-size:.68rem; font-weight:800; text-transform:uppercase; letter-spacing:.06em; color:#4338CA; display:flex; align-items:center; gap:7px; margin-bottom:9px; }
.why p { font-size:.8rem; line-height:1.55; color:#697086; margin:0 0 10px; }
.why__cite { font-size:.7rem; color:#9AA0AC; border-top:1px dashed #E0E3EC; padding-top:9px; line-height:1.45; }
.prefill { margin-top:14px; }
.prefill__h { font-size:.68rem; font-weight:800; text-transform:uppercase; letter-spacing:.06em; color:#0F766E; display:flex; align-items:center; gap:7px; margin-bottom:9px; }
.pfact { display:flex; gap:9px; align-items:flex-start; padding:9px 11px; background:#E4F7F4; border:1px solid #B6EBE4; border-radius:9px; font-size:.8rem; line-height:1.45; color:#0B5D57; margin-bottom:7px; }
.pfact i { color:#14B8A6; flex:none; margin-top:1px; }
.pfact .auto { margin-left:auto; font-size:.58rem; font-weight:800; text-transform:uppercase; color:#0F766E; background:#fff; border:1px solid #B6EBE4; border-radius:999px; padding:1px 6px; flex:none; white-space:nowrap; height:fit-content; }
.safeguards { display:grid; gap:8px; }
.sg { display:flex; gap:10px; align-items:flex-start; padding:10px 12px; background:#E4F7F4; border:1px solid #B6EBE4; border-radius:9px; font-size:.84rem; line-height:1.45; color:#0B5D57; }
.sg i { color:#14B8A6; flex:none; margin-top:1px; }
.sg .auto { margin-left:auto; font-size:.56rem; font-weight:800; text-transform:uppercase; color:#0F766E; background:#fff; border:1px solid #B6EBE4; border-radius:999px; padding:1px 6px; flex:none; white-space:nowrap; height:fit-content; }
.review-summary { border:1px solid #EAECF3; border-radius:12px; overflow:hidden; margin-bottom:16px; }
.rs-row { display:flex; gap:14px; padding:11px 14px; border-bottom:1px solid #EAECF3; font-size:.86rem; }
.rs-row:last-child { border-bottom:none; }
.rs-k { width:110px; flex:none; color:#697086; font-weight:600; }
.rs-v { color:#1A1C23; line-height:1.5; }
.disclaimer { padding:16px 20px; background:linear-gradient(180deg,#FBFAFF,#F8F6FF); border:1px solid #E0D2FB; border-radius:12px; display:flex; gap:13px; align-items:flex-start; font-size:.82rem; line-height:1.6; color:#4B3A73; }
.disclaimer i { color:#7C3AED; flex:none; margin-top:2px; font-size:1.2em; }
.wiz__foot { display:flex; align-items:center; gap:10px; margin-top:22px; padding-top:18px; border-top:1px solid #EAECF3; }
.wiz__foot .save { margin-left:auto; font-size:.75rem; color:#9AA0AC; display:inline-flex; align-items:center; gap:6px; }
.wiz__foot .save i { color:#14B8A6; }
.btn { font-weight:500; font-size:.85rem; padding:.45rem .95rem; border-radius:7px; border:1px solid transparent; cursor:pointer; display:inline-flex; align-items:center; }
.btn-primary { background:#4F46E5; border-color:#4F46E5; color:#fff; }
.btn-primary:hover { background:#4338CA; }
.btn-secondary { color:#697086; border-color:#EAECF3; background:#fff; }
.btn-secondary:hover { background:#EEF0FF; }
</style>
